import React, { createContext, useContext, useReducer } from "react";
import HistorialService from "../services/HistorialService";
import {
  PROGRAMADO_DIA_RECEIVED,
  PROGRAMADO_MES_RECEIVED,
  PROGRAMADO_SEMANA_RECEIVED,
  REAL_DIA_RECEIVED,
  REAL_MES_RECEIVED,
  REAL_SEMANA_RECEIVED,
  RESET_PROGRESO_OBRA,
} from "../actions/types";
import ProyectosReducer from "../reducers/ProyectosReducer";
import AdjuntosService from "../services/AdjuntosService";
import ProgresosService from "../services/ProgresosService";
import ProyectosService from "../services/ProyectosService";
import AvancesService from "../services/AvancesService";
import {
  VIEW_OPTIONS,
  HIDE_OPTIONS,
  EDIT_PROYECTO,
  PROYECTOS_RECEIVED,
  CREATE_NUEVA_PROYECTO,
  SET_PROPERTY_PROYECTO,
  SINGLE_PROYECTO_RECEIVED,
  ULTIMO_AVANCE_RECEIVED,
  AVANCES_DIA_RECEIVED,
  PROYECCION_RECEIVED,
  AVANCE_FINANCIERO_RECEIVED,
  HISTORIAL_RECEIVED,
} from "../types/proyectos";
import { ModalContext } from "./ModalContext";
import { AuthContext } from "./AuthContext";

const initialState = {
  proyectos: null,
  proyecto: null,
  options: null,
  showSpinner: false,
  progresoObra: null,
  ultimoAvance: undefined,
  avancesDia: undefined,
  proyeccion: undefined,
  avanceFinanciero: undefined,
  historial: null,
  historialHasMore: false,
};

export const ProyectosContext = createContext(initialState);

export const ProyectosProvider = ({ children }) => {
  const [state, dispatch] = useReducer(ProyectosReducer, initialState);

  const { user } = useContext(AuthContext);

  const { alert, success, clearModal } = useContext(ModalContext);

  const getProyectos = (filters) => {
    ProyectosService.getProyectos(filters)
      .then((res) => {
        const { proyectos } = res.data;
        dispatch({ type: PROYECTOS_RECEIVED, payload: proyectos });
      })
      .catch((error) => {
        alert(error);
      });
  };

  const getAllProyectos = () => {
    ProyectosService.getAllProyectos()
      .then((res) => {
        const { proyectos } = res.data;
        dispatch({ type: PROYECTOS_RECEIVED, payload: proyectos });
      })
      .catch((error) => {
        alert(error);
      });
  };

  const getSingleProyecto = (idProyecto) => {
    ProyectosService.getProyecto(idProyecto).then((res) => {
      const { proyecto } = res.data;
      if (user.permiso === "admin") {
        proyecto.permiso = user.permiso;
      }      
      dispatch({ type: SINGLE_PROYECTO_RECEIVED, payload: proyecto });
    });
  };

  // Latest avance of the project (any level) with the user who registered it.
  const getUltimoAvance = (idProyecto) => {
    dispatch({ type: ULTIMO_AVANCE_RECEIVED, payload: undefined });
    AvancesService.getUltimoAvance(idProyecto)
      .then((res) => {
        dispatch({
          type: ULTIMO_AVANCE_RECEIVED,
          payload: res.data.avance || null,
        });
      })
      .catch(() => {
        dispatch({ type: ULTIMO_AVANCE_RECEIVED, payload: null });
      });
  };

  const getProgresoObra = (idProyecto, filters) => {
    dispatch({ type: RESET_PROGRESO_OBRA });
    ProgresosService.getProgreso(idProyecto, filters).then((res) => {
      const { semana, mes, dia } = res.data.progreso;
      //Set progreso semana
      dispatch({ type: REAL_SEMANA_RECEIVED, payload: semana.real });
      dispatch({
        type: PROGRAMADO_SEMANA_RECEIVED,
        payload: semana.programado,
      });

      //Set progreso dia
      dispatch({ type: REAL_DIA_RECEIVED, payload: dia.real });
      dispatch({
        type: PROGRAMADO_DIA_RECEIVED,
        payload: dia.programado,
      });

      //Set progreso mes
      dispatch({ type: REAL_MES_RECEIVED, payload: mes.real });
      dispatch({
        type: PROGRAMADO_MES_RECEIVED,
        payload: mes.programado,
      });
    });
  };

  const createProyecto = () => {
    dispatch({
      type: CREATE_NUEVA_PROYECTO,
    });
  };

  const editProyecto = (proyecto) => {
    dispatch({ type: EDIT_PROYECTO, payload: proyecto });
  };

  const setPropiedadProyecto = (key, value) => {
    dispatch({ type: SET_PROPERTY_PROYECTO, payload: { key, value } });
  };

  const clearProyectos = () => {
    dispatch({ type: PROYECTOS_RECEIVED, payload: null });
  };

  const clearSingleProyecto = () => {
    dispatch({ type: SINGLE_PROYECTO_RECEIVED, payload: null });
  };

  const viewOptions = (proyecto) => {
    dispatch({ type: VIEW_OPTIONS, payload: proyecto });
  };

  const hideOptions = () => {
    dispatch({ type: HIDE_OPTIONS });
  };

  const postProyecto = (proyecto, callback) => {
    let service = ProyectosService.putProyecto;
    if (isNaN(parseInt(proyecto.idProyecto))) {
      service = ProyectosService.postProyecto;
    }
    const promises = [];
    if (proyecto.file && proyecto.file !== null) {
      const formData = AdjuntosService.getFormData(proyecto.file);
      promises.push(
        new Promise((resolve, reject) => {
          AdjuntosService.postAdjunto(formData).then((res) => {
            const { idAdjunto } = res.data;
            proyecto.idAdjunto = idAdjunto;
            delete proyecto.file;
            resolve();
          });
        })
      );
    }
    Promise.all(promises).then(() => {
      service(proyecto)
        .then(() => {
          clearModal();
          success(`Proyecto ${proyecto.nombre} guardado.`);
          if (callback) callback();
        })
        .catch(alert);
    });
  };

  const deleteProyecto = (idProyecto, callback) => {
    ProyectosService.deleteProyecto(idProyecto)
      .then(() => {
        success("Proyecto eliminado.");
        if (callback) callback();
      })
      .catch((error) => {
        alert(error);
      });
  };

  // Avances of a day range, grouped per element with the net delta.
  const getAvancesDia = (idProyecto, desde, hasta) => {
    dispatch({ type: AVANCES_DIA_RECEIVED, payload: undefined });
    AvancesService.getAvancesDia(idProyecto, desde, hasta)
      .then((res) =>
        dispatch({ type: AVANCES_DIA_RECEIVED, payload: res.data.avances })
      )
      .catch((error) => {
        dispatch({ type: AVANCES_DIA_RECEIVED, payload: null });
        alert(error);
      });
  };

  // Fecha esperada de terminación as of `fecha` (today when not given).
  const getProyeccion = (idProyecto, fecha) => {
    dispatch({ type: PROYECCION_RECEIVED, payload: undefined });
    ProgresosService.getProyeccion(idProyecto, fecha)
      .then((res) =>
        dispatch({ type: PROYECCION_RECEIVED, payload: res.data.proyeccion })
      )
      .catch(() => dispatch({ type: PROYECCION_RECEIVED, payload: null }));
  };

  // Avance financiero as of `fecha` (today when not given).
  const getAvanceFinanciero = (idProyecto, fecha) => {
    dispatch({ type: AVANCE_FINANCIERO_RECEIVED, payload: undefined });
    ProgresosService.getAvanceFinanciero(idProyecto, fecha)
      .then((res) =>
        dispatch({ type: AVANCE_FINANCIERO_RECEIVED, payload: res.data.avance })
      )
      .catch(() =>
        dispatch({ type: AVANCE_FINANCIERO_RECEIVED, payload: null })
      );
  };

  // Change history of the obra's partidas and destajos (paged).
  const getHistorialProyecto = (idProyecto, offset = 0, limit = 100) => {
    if (offset === 0) dispatch({ type: HISTORIAL_RECEIVED, payload: null });
    HistorialService.getHistorialProyecto(idProyecto, { limit, offset })
      .then((res) => {
        const items = res.data.historial;
        dispatch({
          type: HISTORIAL_RECEIVED,
          payload: { items, offset, hasMore: items.length === limit },
        });
      })
      .catch(alert);
  };

  // Sets one preference of the obra (modo_progreso, tipo_captura, ...) and
  // saves it.
  const updatePreferenciaProyecto = (idProyecto, key, value) => {
    setPropiedadProyecto(key, value);
    if (!idProyecto) return;
    ProyectosService.putProyecto({ idProyecto, [key]: value }).catch((error) =>
      alert(
        error?.response?.data || "No se pudo guardar la preferencia de la obra."
      )
    );
  };

  return (
    <ProyectosContext.Provider
      value={{
        ...state,
        getAvancesDia,
        getProyeccion,
        getAvanceFinanciero,
        getHistorialProyecto,
        updatePreferenciaProyecto,
        viewOptions,
        hideOptions,
        postProyecto,
        getProyectos,
        editProyecto,
        deleteProyecto,
        clearProyectos,
        createProyecto,
        getAllProyectos,
        getProgresoObra,
        getUltimoAvance,
        getSingleProyecto,
        clearSingleProyecto,
        setPropiedadProyecto,
      }}
    >
      {children}
    </ProyectosContext.Provider>
  );
};
